'use client';

import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText, AlertCircle, Scale, Info, CheckCircle } from 'lucide-react';
import Footer from '@/components/Footer';


export default function TermosPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-slate-200 selection:text-slate-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-700 to-slate-900 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-center space-y-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 px-6 py-3 rounded-2xl text-white font-black uppercase tracking-tighter italic transition-all hover:-translate-x-1 active:scale-95 mb-8 group shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Voltar para o Início
          </Link>
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic">
            Termos de Uso <br className="md:hidden" />
            <span className="text-slate-400">Catanduvas PR</span>
          </h1>
          <p className="text-slate-300 text-lg md:text-xl font-medium max-w-2xl mx-auto">
            Leia atentamente as regras e condições de uso do portal CatanduvasPR.com.br.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 -mt-8 pb-20">
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl border border-slate-100 space-y-12">
          
          {/* Introdução */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-3 rounded-2xl">
                <FileText className="w-8 h-8 text-slate-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">1. Aceitação dos Termos</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Ao acessar e utilizar o portal <strong>CatanduvasPR.com.br</strong>, você concorda em cumprir e estar vinculado aos seguintes termos e condições de uso. Se você não concordar com qualquer parte destes termos, não deverá utilizar nosso portal.
              </p>
              <p>
                Estes termos podem ser atualizados a qualquer momento sem aviso prévio. Recomendamos a consulta periódica desta página para estar ciente de eventuais mudanças.
              </p>
            </div>
          </section>

          {/* Utilidade Pública */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-2xl">
                <Info className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">2. Natureza da Informação</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                O <strong>CatanduvasPR.com.br</strong> é um portal de <strong>utilidade pública independente</strong>. As informações aqui contidas (horários de ônibus, plantões de farmácias, telefones úteis, etc.) são coletadas de fontes públicas e de terceiros.
              </p>
              <p>
                Embora façamos o máximo esforço para manter as informações atualizadas e precisas, não garantimos a exatidão absoluta de todos os dados. O usuário deve sempre confirmar informações críticas diretamente com os prestadores de serviços.
              </p>
            </div>
          </section>

          {/* Limitação de Responsabilidade */}
          <section className="space-y-6 p-8 bg-amber-50 rounded-[2rem] border border-amber-100">
            <div className="flex items-center gap-3">
              <div className="bg-amber-600 p-3 rounded-2xl">
                <AlertCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">3. Limitação de Responsabilidade</h2>
            </div>
            <div className="text-slate-600 font-medium leading-relaxed space-y-4">
              <p>
                O portal e seus administradores (<strong>Estúdio Mídia Publicidades</strong>) não se responsabilizam por:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Danos diretos ou indiretos decorrentes do uso das informações do portal;</li>
                <li>Alterações repentinas em horários de ônibus ou plantões de farmácias;</li>
                <li>Qualidade dos serviços prestados por empresas listadas no Guia Comercial;</li>
                <li>Indisponibilidade técnica temporária do portal.</li>
              </ul>
            </div>
          </section>

          {/* Propriedade Intelectual */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-3 rounded-2xl">
                <ShieldCheck className="w-8 h-8 text-slate-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">4. Propriedade Intelectual</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Todo o conteúdo original deste portal (textos, design, logotipos e organização de dados) é de propriedade do <strong>Estúdio Mídia Publicidades</strong> ou de seus parceiros e está protegido pelas leis de direitos autorais brasileiras.
              </p>
              <p>
                A reprodução parcial ou total do conteúdo para fins comerciais sem autorização prévia é expressamente proibida.
              </p>
            </div>
          </section>

          {/* Uso do Formulário */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-2xl">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">5. Conduta do Usuário</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Ao utilizar nossos formulários de contato, você se compromete a fornecer informações verdadeiras e a não utilizar a ferramenta para o envio de spam, conteúdo ofensivo ou ilegal.
              </p>
            </div>
          </section>

          {/* Foro */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-3 rounded-2xl">
                <Scale className="w-8 h-8 text-slate-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">6. Foro e Legislação</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                Estes termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o foro da Comarca de Catanduvas, PR, para dirimir quaisquer controvérsias decorrentes deste documento.
              </p>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
}
